import React from 'react';
import { Box, Tooltip, alpha, type Theme } from '@mui/material';
import { GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';

/**
 * Shared chrome for every DataGrid on the Dialing Opportunity page (state
 * breakdown, by-agent, lead geography mix, lead-level detail), so a style
 * change here can't drift between tables. Header: sunken background,
 * uppercase/letter-spaced titles, fixed 36px height. Rows: divider cell
 * borders, primary-tinted hover, and an opt-in "selected" row class shared by
 * every clickable table (`dialop-row-selected`).
 */
export function buildDialOpGridSx(theme: Theme, opts: { clickable?: boolean } = {}) {
  return {
    border: 0,
    borderRadius: 0,
    fontSize: '0.8125rem',
    '& .MuiDataGrid-columnHeaders': {
      bgcolor: theme.tokens?.surface?.sunken ?? 'action.hover',
      borderBottom: `1px solid ${theme.palette.divider}`,
      minHeight: '36px !important',
      maxHeight: '36px !important',
    },
    '& .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': { outline: 'none' },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 600,
      fontSize: '0.7rem',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: 'text.secondary',
    },
    '& .MuiDataGrid-cell': {
      borderColor: theme.palette.divider,
      '&:focus, &:focus-within': { outline: 'none' },
    },
    '& .MuiDataGrid-row': opts.clickable ? { cursor: 'pointer' } : {},
    '& .MuiDataGrid-row:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.04),
    },
    '& .MuiDataGrid-row.dialop-row-selected': {
      bgcolor: alpha(theme.palette.primary.main, 0.1),
    },
    '& .MuiDataGrid-footerContainer': {
      borderTop: `1px solid ${theme.palette.divider}`,
      minHeight: 52,
    },
    '& .MuiTablePagination-root': {
      fontSize: '0.75rem',
    },
  } as const;
}

/** Every DataGrid on this page uses the same [10, 25, 50] page-size ladder. */
export const DIAL_OP_PAGE_SIZE_OPTIONS = [10, 25, 50];

/**
 * Same visual recipe as ``buildDialOpGridSx``'s header, but for the plain
 * MUI ``Table``/``TableHead`` used by the (small, hardcoded, unpaginated)
 * Smart Scheduling preview tables -- so a header looks the same whether the
 * table underneath it is a DataGrid or a plain Table. Spread onto the
 * ``TableHead``'s ``sx`` prop.
 */
export function buildDialOpTableHeadSx(theme: Theme) {
  return {
    bgcolor: theme.tokens?.surface?.sunken ?? 'action.hover',
    '& .MuiTableCell-root': {
      height: 36,
      py: 0.75,
      fontWeight: 600,
      fontSize: '0.7rem',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  } as const;
}

export function HeaderWithTooltip({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        component="span"
        sx={{
          borderBottom: '1px dashed',
          borderColor: 'text.disabled',
          cursor: 'help',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}

/**
 * Search placeholder text is set via the DataGrid's own
 * `localeText={{ toolbarQuickFilterPlaceholder: '...' }}`, not here -- keep
 * that per-table for a relevant hint ("Search states…" vs "Search agents…").
 */
export function DialOpGridToolbar() {
  return (
    <GridToolbarContainer
      sx={{ px: 2, py: 1, gap: 1.5, borderBottom: 1, borderColor: 'divider', justifyContent: 'flex-end' }}
    >
      <Box sx={{ width: { xs: '100%', sm: 280 } }}>
        <GridToolbarQuickFilter debounceMs={250} />
      </Box>
    </GridToolbarContainer>
  );
}
